import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import Icon from './Icon';
import { Card } from './ui';
import { cn } from '@/lib/utils';

export function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return iso;
  }
}

export function fmtViews(n) {
  if (!n) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export function viewsLabel(n) {
  const c = n || 0;
  return `${fmtViews(c)} ${c === 1 ? 'view' : 'views'}`;
}

export function FeatureCard({ icon, title, text, className }) {
  return (
    <Card className={cn('group hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg', className)}>
      <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{text}</p>
    </Card>
  );
}

export function SegmentCard({ seg, ctaLabel = 'Explore' }) {
  return (
    <Link
      to={`/solutions/${seg.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/[0.03]"
    >
      <div className="relative h-44 overflow-hidden">
        <img src={seg.image} alt={seg.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white">
            <Icon name={seg.icon} className="h-4.5 w-4.5" />
          </span>
          <span className="font-display text-lg font-bold text-white">{seg.name}</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{seg.tagline}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          {ctaLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function CaseStudyCard({ cs }) {
  return (
    <Link
      to={`/case-studies/${cs.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/[0.03]"
    >
      <div className="relative h-48 overflow-hidden">
        <img src={cs.image} alt={cs.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800 backdrop-blur dark:bg-black/60 dark:text-white">{cs.segment}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-lg font-bold leading-snug text-slate-900 dark:text-white">{cs.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{cs.excerpt}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1">
          {cs.metrics.slice(0, 3).map((m) => (
            <div key={m.label}>
              <div className="font-display text-lg font-bold text-brand">{m.value}</div>
              <div className="text-[11px] text-slate-400 dark:text-slate-500">{m.label}</div>
            </div>
          ))}
        </div>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          Read the story <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          {cs.views ? <span className="ml-auto text-[11px] font-normal text-slate-400">{fmtViews(cs.views)} {cs.views === 1 ? 'view' : 'views'}</span> : null}
        </span>
      </div>
    </Link>
  );
}

export function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/[0.03]"
    >
      <div className="relative h-44 overflow-hidden">
        <img src={post.image} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        <span className="absolute left-4 top-4 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">{post.category}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 text-xs text-slate-400 dark:text-slate-500">{formatDate(post.date)} · {post.readTime} min read{post.views ? ` · ${viewsLabel(post.views)}` : ''}</div>
        <h3 className="font-display text-base font-bold leading-snug text-slate-900 dark:text-white group-hover:text-brand">{post.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300 line-clamp-3">{post.excerpt}</p>
      </div>
    </Link>
  );
}

export function CheckList({ items, className }) {
  return (
    <ul className={cn('space-y-3', className)}>
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
            <Check className="h-3 w-3" />
          </span>
          {it}
        </li>
      ))}
    </ul>
  );
}
